<template>
  <v-tab-item>
    <v-card>
      <v-data-table
        :headers="seminarEmailHeaders"
        :items="seminarEmailItems"
        :loading="$apollo.queries.seminarEmails?.loading"
        :items-per-page="-1"
        class="elevation-1"
        item-key="id"
      >
        <template #top>
          <v-toolbar flat>
            <v-toolbar-title>{{ $t('Emails') }}</v-toolbar-title>
            <v-spacer />
            <v-btn
              @click="$apollo.queries.seminarEmails.refetch({ context: apolloHeaders() })"
              :title="$t('refreshData')"
              fab
              icon
              elevation="2"
              small
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
            <v-btn
              color="success"
              class="ml-5"
              @click="createSeminarEmail()"
            >
              {{ $t('createEmail') }}
            </v-btn>
          </v-toolbar>
        </template>

        <template #item.sentAt="{ item }">
          <span v-if="item.sentAt">{{ formatDateTime(item.sentAt, 'L LT') }}</span>
          <v-chip
            v-else
            small
          >{{ $t('Not sent yet') }}</v-chip>
        </template>

        <template #item.createdAt="{ item }">
          am <strong>{{ formatDateTime(item.createdAt, 'L LT') }}</strong><br />
          von <strong>{{ item.createdBy }}</strong><br />
        </template>
        <template #item.updatedAt="{ item }">
          am <strong>{{ formatDateTime(item.updatedAt, 'L LT') }}</strong><br />
          von <strong>{{ item.updatedBy }}</strong><br />
        </template>

        <template #item.action="{ item }">
          <ActionCell
            v-if="!item.sentAt"
            :handle-edit="() => editSeminarEmail(item)"
            :handle-delete="() => deleteSeminarEmail(item)"
            :handle-send="() => sendSeminarEmail(item)"
          />
          <ActionCell
            v-else
            :handle-show="() => showSeminarEmail(item)"
          />
        </template>
      </v-data-table>
    </v-card>

    <SeminarEmailDialog
      v-model="showSeminarEmailDialog"
      :email-item="seminarEmailDialogItem"
      :create-email="performCreateSeminarEmail"
      :update-email="performUpdateSeminarEmail"
    />
  </v-tab-item>
</template>

<script>
  import gql from 'graphql-tag';
  import { formatDateTime } from '@/utils/dates';
  import NotificationMixin from '@/mixins/NotificationMixin';
  import TenantMixin from '@/mixins/TenantMixin';
  import ActionCell from '@/components/ActionCell';
  import SeminarEmailDialog from '@/components/seminarEmail/Dialog';

  export default {
    name: 'SeminarEmailTab',
    components: { ActionCell, SeminarEmailDialog },
    mixins: [NotificationMixin, TenantMixin],
    props: {
      seminar: { type: Object, required: true }
    },
    data() {
      return {
        showSeminarEmailDialog: false,
        seminarEmailDialogItem: {},
        seminarEmails: { totalCount: 0, edges: [] },
        seminarEmailHeaders: [
          { text: this.$t('subject'), value: 'subject' },
          { text: this.$t('from'), value: 'fromAddress' },
          { text: this.$t('sentAt'), value: 'sentAt' },
          { text: this.$t('created'), value: 'createdAt' },
          { text: this.$t('updated'), value: 'updatedAt' },
          { text: this.$t('Actions'), value: 'action', sortable: false, align: 'center' }
        ]
      };
    },
    computed: {
      seminarEmailItems() {
        const edges = this.seminarEmails?.edges ?? [];
        return edges.map(({ node }) => ({
          id: node.id,
          subject: node.subject,
          fromAddress: node.fromAddress,
          sentAt: node.sentAt,
          createdAt: node.createdAt,
          createdBy: node.createdByEmail,
          updatedAt: node.updatedAt,
          updatedBy: node.updatedByEmail,
          body: node.body,
          sendToNewParticipants: node.sendToNewParticipants,
          attachments: (node.attachments?.edges ?? []).map(({ node: attachment }) => ({
            id: attachment.id,
            name: attachment.name,
            filePath: attachment.filePath,
          })),
        }));
      }
    },
    methods: {
      formatDateTime,

      apolloHeaders() {
        const token = this.$store.state.authentication.loggedIn
          ? `Bearer ${this.$store.state.authentication.token}`
          : '';
        const headers = {};
        if (token) headers.authorization = token;
        return { headers };
      },

      // UI handlers
      createSeminarEmail() {
        this.seminarEmailDialogItem = {
          subject: '',
          body: '',
          sendToNewParticipants: false,
          seminar: this.seminar['@id']
        };
        this.showSeminarEmailDialog = true;
      },
      editSeminarEmail(item) {
        this.seminarEmailDialogItem = {
          ...item,
          ['@id']: item.id,
          seminar: this.seminar['@id']
        };
        this.showSeminarEmailDialog = true;
      },
      showSeminarEmail(item) {
        // @TODO: disable all stuff
        this.editSeminarEmail(item);
      },

      // mutations
      sendSeminarEmail(item) {
        this.$confirm(this.$t('ConfirmSendEmail'), {
          buttonTrueText: this.$t('Yes'),
          buttonFalseText: this.$t('No')
        }).then((ok) => {
          if (!ok) return;

          const SEND = gql`
          mutation SendSeminarEmail($id: ID!) {
            sendSeminarEmail(input: { id: $id }) {
              seminarEmail { id sentAt }
            }
          }
        `;

          this.$apollo.mutate({
            mutation: SEND,
            variables: { id: item.id },
            context: this.apolloHeaders(),
            refetchQueries: [
              {
                query: this.$options.apollo.seminarEmails.query,
                variables: { id: this.seminar['@id'] },
                context: this.apolloHeaders()
              }
            ]
          })
            .then(() => this.showMessage(this.$t('EmailSent')))
            .catch((e) => this.showError(e?.graphQLErrors?.[0]?.message || this.$t('EmailSendFailed')));
        });
      },

      deleteSeminarEmail(item) {
        const DEL = gql`
        mutation DeleteSeminarEmail($id: ID!, $clientMutationId: String!) {
          deleteSeminarEmail(input: { id: $id, clientMutationId: $clientMutationId }) {
            clientMutationId
          }
        }
      `;

        this.$apollo.mutate({
          mutation: DEL,
          variables: { id: item.id, clientMutationId: 'delete-' + item.id },
          context: this.apolloHeaders(),
          refetchQueries: [
            {
              query: this.$options.apollo.seminarEmails.query,
              variables: { id: this.seminar['@id'] },
              context: this.apolloHeaders()
            }
          ]
        })
          .then(() => this.showMessage(this.$t('EmailDeleted')))
          .catch((e) => this.showError(e?.graphQLErrors?.[0]?.message || this.$t('EmailDeleteFailed')));
      },

      performCreateSeminarEmail({ item, payload }) {
        const CREATE = gql`
        mutation CreateSeminarEmail($input: createSeminarEmailInput!) {
          createSeminarEmail(input: $input) {
            seminarEmail {
              id
              subject
              fromName
              fromAddress
              body
              sendToNewParticipants
              seminar { id }
              attachments(first: 500) { totalCount edges { node { id name } } }
              recipients(first: 500) { totalCount edges { node { id address name } } }
            }
          }
        }
      `;

        const input = {
          subject: payload.subject,
          body: payload.body,
          seminar: item.seminar,
          sendToNewParticipants: payload.sendToNewParticipants || false,
          recipients: item.recipients || [],
          attachments: item.attachments || []
        };

        return this.$apollo.mutate({
          mutation: CREATE,
          variables: { input },
          context: this.apolloHeaders(),
          refetchQueries: [
            {
              query: this.$options.apollo.seminarEmails.query,
              variables: { id: this.seminar['@id'] },
              context: this.apolloHeaders()
            }
          ]
        })
          .then((res) => {
            this.showMessage(this.$t('successfullyCreated'));
            return res;
          })
          .catch((e) => {
            const msg = e?.graphQLErrors?.[0]?.message || e?.networkError?.result?.errors?.[0]?.message || e?.message;
            this.showError(msg || this.$t('Submit failed'));
            return Promise.reject(e);
          });
      },

      performUpdateSeminarEmail({ item, payload }) {
        const UPDATE = gql`
        mutation UpdateSeminarEmail($input: updateSeminarEmailInput!) {
          updateSeminarEmail(input: $input) {
            seminarEmail {
              id
              subject
              fromName
              fromAddress
              body
              sendToNewParticipants
              seminar { id }
              attachments(first: 500) { totalCount edges { node { id name } } }
              recipients(first: 500) { totalCount edges { node { id address name } } }
            }
          }
        }
      `;

        const input = {
          id: item['@id'],
          subject: payload.subject,
          body: payload.body,
          sendToNewParticipants: payload.sendToNewParticipants || false,
        };

        return this.$apollo.mutate({
          mutation: UPDATE,
          variables: { input },
          context: this.apolloHeaders(),
          refetchQueries: [
            {
              query: this.$options.apollo.seminarEmails.query,
              variables: { id: this.seminar['@id'] },
              context: this.apolloHeaders()
            }
          ]
        })
          .then((res) => {
            this.showMessage(this.$t('successfullyUpdated'));
            this.$apollo.queries.seminarEmails.refetch({ context: this.apolloHeaders() });
            return res;
          })
          .catch((e) => {
            const msg = e?.graphQLErrors?.[0]?.message || e?.message || this.$t('Submit failed');
            this.showError(msg);
            return Promise.reject(e);
          });
      },

    },

    apollo: {
      seminarEmails: {
        query: gql`
        query SeminarEmails($id: ID!) {
          seminar(id: $id) {
            id
            seminarEmails(order: { createdAt: "DESC" }) {
              totalCount
              edges {
                node {
                  id
                  subject
                  fromAddress
                  sentAt
                  createdAt
                  updatedAt
                  body
                  sendToNewParticipants
                  createdByEmail
                  updatedByEmail
                  attachments(first: 500) {
                    totalCount
                    edges {
                      node {
                        id
                        name
                        filePath
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `,
        variables() {
          return { id: this.seminar['@id'] };
        },
        context() {
          return this.apolloHeaders();
        },
        update: ({ seminar }) => seminar?.seminarEmails ?? { totalCount: 0, edges: [] }
      }
    }
  };
</script>
